import {
  Camera,
  Client,
  deleteMission,
  getMission,
  GetMissionResponse,
  getRun,
  GetRunResponse,
  listCameras,
  ListCamerasResponse,
  listMissions,
  ListMissionsResponse,
  listMissionsExpanded,
  ListMissionsExpandedResponse,
  listRuns,
  ListRunsResponse,
  Mission,
  Run,
  patchMission,
  PatchMissionResponse,
  patchRun,
  PatchRunResponse,
  PagedListCamera,
  deleteRun,
} from '../generated/index';
import {
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
  QueryClient,
} from '@tanstack/solid-query';
import { Accessor } from 'solid-js';
import { returnOrThrow } from '@trimble/utilities';

export interface DeleteMissionParams {
  readonly missionTrn: string;
  readonly name: string;
  readonly tenantTrn: string;
  readonly projectId: string | undefined;
}

export interface DeleteRunParams {
  readonly runId: string;
  readonly missionTrn: string;
  readonly name: string;
  readonly tenantTrn: string;
  readonly projectId: string | undefined;
}

export type MissionDto = Mission;
export type RunDto = Run;
export type CameraDto = Camera;

export type MissionTreeResponse = {
  missions: ReadonlyArray<MissionTreeItem>;
};

export type MissionTreeItem = {
  mission: MissionDto;
  runs: ReadonlyArray<RunDto>;
};

export class MobileMappingApiService {
  constructor(
    private readonly client: Client,
    private readonly queryClient: QueryClient
  ) {}

  //#region MISSIONS
  public listMissionsQuery(
    tenantTrn: Accessor<string | null | undefined>
  ): UseQueryResult<ListMissionsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['missions', tenantTrn()],
      queryFn: async (): Promise<ListMissionsResponse> => {
        // TODO: handle pagination
        const response = await listMissions({
          path: { tenantTrn: tenantTrn()! },
          client: this.client,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase TODO: FIX on API side
          query: { PageSize: 500 },
        });

        return returnOrThrow(response, 'Error while listing missions');
      },
      enabled: tenantTrn() != undefined,
    }));
  }

  public getMissionQuery(
    tenantTrn: Accessor<string | null | undefined>,
    missionTrn: Accessor<string | null | undefined>
  ): UseQueryResult<GetMissionResponse, Error> {
    return useQuery(() => ({
      queryKey: ['mission', tenantTrn(), missionTrn()],
      queryFn: async (): Promise<MissionDto> => {
        const response = await getMission({
          path: {
            tenantTrn: tenantTrn()!,
            missionTrn: missionTrn()!,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while getting mission');
      },
      enabled: tenantTrn() != undefined && missionTrn() != undefined,
    }));
  }

  public async deleteMission(
    tenantTrn: string,
    missionTrn: string,
    permanent?: boolean
  ): Promise<unknown> {
    const response = await deleteMission({
      path: { tenantTrn, missionTrn },
      // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase
      query: { Permanent: permanent },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while deleting a mission');
  }

  public async patchMission(
    tenantTrn: string,
    missionTrn: string,
    body: { name: string }
  ): Promise<PatchMissionResponse> {
    const response = await patchMission({
      path: { tenantTrn, missionTrn },
      body,
      client: this.client,
    });

    return returnOrThrow(response, 'Error while updating mission');
  }

  public deleteMissionMutation(): UseMutationResult<
    unknown,
    Error,
    DeleteMissionParams
  > {
    return useMutation(() => ({
      mutationFn: async (params: DeleteMissionParams): Promise<unknown> =>
        this.deleteMission(params.tenantTrn, params.missionTrn, true),
      onSuccess: (_data: unknown, params: DeleteMissionParams) => {
        void this.queryClient.invalidateQueries({
          queryKey: ['missionsWithRuns', params.tenantTrn],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['alignments', params.tenantTrn],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['pointClouds', params.projectId],
        });
      },
    }));
  }

  public listMissionsTreeQuery(
    tenantTrn: Accessor<string | null | undefined>
  ): UseQueryResult<MissionTreeResponse, Error> {
    return useQuery(() => ({
      queryKey: ['missionsWithRuns', tenantTrn()],
      queryFn: async (): Promise<MissionTreeResponse> => {
        const response = await listMissionsExpanded({
          path: { tenantTrn: tenantTrn()! },
          client: this.client,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase TODO: FIX on API side
          query: { PageSize: 500 },
        });
        const data: ListMissionsExpandedResponse = returnOrThrow(
          response,
          'Error while listing missions with runs'
        );

        const missionsWithRuns = (data.items ?? []).map((missionExpanded) => ({
          mission: missionExpanded as MissionDto,
          runs: (missionExpanded.runs ?? []).map(
            (run): RunDto => ({
              ...run,
              missionTrn: missionExpanded.missionTrn,
            })
          ),
        }));

        return { missions: missionsWithRuns };
      },
      enabled: tenantTrn() != undefined,
    }));
  }
  //#endregion

  //#region RUNS
  public listRunsQuery(
    tenantTrn: Accessor<string | null | undefined>,
    missionTrn: Accessor<string | null | undefined>
  ): UseQueryResult<ListRunsResponse, Error> {
    return useQuery(() => ({
      queryKey: ['runs', tenantTrn(), missionTrn()],
      queryFn: async (): Promise<ListRunsResponse> => {
        // TODO: handle pagination
        const response = await listRuns({
          path: {
            tenantTrn: tenantTrn()!,
            missionTrn: missionTrn()!,
          },
          client: this.client,
          // eslint-disable-next-line @typescript-eslint/naming-convention -- API requires PascalCase TODO: FIX on API side
          query: { PageSize: 500 },
        });

        return returnOrThrow(response, 'Error while listing runs');
      },
      enabled: tenantTrn() != undefined && missionTrn() != undefined,
    }));
  }

  public getRunQuery(
    tenantTrn: Accessor<string | null | undefined>,
    missionTrn: Accessor<string | null | undefined>,
    runId: Accessor<string | null | undefined>
  ): UseQueryResult<GetRunResponse, Error> {
    return useQuery(() => ({
      queryKey: ['mission', tenantTrn(), missionTrn(), runId()],
      queryFn: async (): Promise<RunDto> => {
        const response = await getRun({
          path: {
            tenantTrn: tenantTrn()!,
            missionTrn: missionTrn()!,
            runId: runId()!,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while getting run');
      },
      enabled:
        tenantTrn() != undefined &&
        missionTrn() != undefined &&
        runId() != undefined,
    }));
  }

  public async deleteRun(
    tenantTrn: string,
    missionTrn: string,
    runId: string
  ): Promise<unknown> {
    const response = await deleteRun({
      path: {
        tenantTrn,
        missionTrn,
        runId,
      },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while deleting a run');
  }

  public deleteRunMutation(): UseMutationResult<
    unknown,
    Error,
    DeleteRunParams
  > {
    return useMutation(() => ({
      mutationFn: async (params: DeleteRunParams): Promise<unknown> =>
        this.deleteRun(params.tenantTrn, params.missionTrn, params.runId),
      onSuccess: (_data: unknown, params: DeleteRunParams) => {
        void this.queryClient.invalidateQueries({
          queryKey: ['missionsWithRuns', params.tenantTrn],
        });
        void this.queryClient.invalidateQueries({
          queryKey: ['pointClouds', params.projectId],
        });
      },
    }));
  }

  public async patchRun(
    tenantTrn: string,
    missionTrn: string,
    runId: string,
    body: { name: string }
  ): Promise<PatchRunResponse> {
    const response = await patchRun({
      path: {
        tenantTrn,
        missionTrn,
        runId,
      },
      body,
      client: this.client,
    });

    return returnOrThrow(response, 'Error while updating run');
  }

  public async fetchRun(
    tenantTrn: string,
    missionTrn: string,
    runId: string
  ): Promise<RunDto> {
    return this.queryClient.fetchQuery({
      queryKey: ['run', tenantTrn, missionTrn, runId],
      staleTime: 10_000, // Cache for 10 seconds to prevent API abuse
      queryFn: async (): Promise<RunDto> => {
        const response = await getRun({
          path: {
            tenantTrn,
            missionTrn,
            runId,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while fetching run');
      },
    });
  }
  //#endregion

  //#region CAMERAS
  public listCamerasReactiveQuery(
    tenantTrn: Accessor<string | null | undefined>,
    missionTrn: Accessor<string | null | undefined>,
    runId: Accessor<string | null | undefined>
  ): UseQueryResult<ListCamerasResponse, Error> {
    return useQuery(() => ({
      queryKey: ['cameras', tenantTrn(), missionTrn(), runId()],
      queryFn: async (): Promise<ListCamerasResponse> => {
        const response = await listCameras({
          path: {
            tenantTrn: tenantTrn()!,
            missionTrn: missionTrn()!,
            runId: runId()!,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while listing cameras');
      },
      enabled:
        tenantTrn() != undefined &&
        missionTrn() != undefined &&
        runId() != undefined,
    }));
  }

  public listCamerasQuery(
    tenantTrn: string,
    missionTrn: string,
    runId: string
  ): Promise<PagedListCamera> {
    return this.queryClient.fetchQuery({
      queryKey: ['cameras', tenantTrn, missionTrn, runId],
      queryFn: async (): Promise<ListCamerasResponse> => {
        const response = await listCameras({
          path: {
            tenantTrn,
            missionTrn,
            runId,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while listing cameras');
      },
    });
  }
  //#endregion
}
